import { OrderHeader } from '@epicuro-next/data/order-book';
import {
  IOrderItemWithProductInfo,
  IOrderProduct,
} from '@epicuro-next/models/order-book';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import { Order, OrderItem } from 'graphql-api/baskets-api-interfaces';

import * as fromActions from '../actions/order-details.actions';

export const orderDetailsFeatureKey = 'order-details';

export const selectOrderItemUniqueId = (
  orderItem: OrderItem | IOrderItemWithProductInfo,
) => {
  const requestedDateSuffix = orderItem.requestedDeliveryDate
    ? `-${orderItem.requestedDeliveryDate}`
    : '';
  return `${orderItem.id}${requestedDateSuffix}`;
};

export interface OrderDetailsState extends EntityState<OrderItem> {
  details: Order | null;
  products: IOrderProduct[];
  orderHeader: OrderHeader | null;
  loading: boolean;
  orderHeaderLoading: boolean;
  error: string | null;
  loadingIds: string[]; // order items that are loading with extra data
}

export const adapter: EntityAdapter<OrderItem> = createEntityAdapter<OrderItem>(
  {
    selectId: selectOrderItemUniqueId,
  },
);

export const initialState: OrderDetailsState = adapter.getInitialState({
  details: null,
  products: [],
  orderHeader: null,
  loading: false,
  orderHeaderLoading: false,
  error: null,
  loadingIds: [],
  expandedIds: [],
});

export const reducer = createReducer<OrderDetailsState>(
  initialState,
  on(
    fromActions.loadOrderDetails,
    (state): OrderDetailsState => ({
      ...state,
      loading: true,
      error: null,
    }),
  ),
  on(
    fromActions.downloadOrderInvoices,
    (state): OrderDetailsState => ({
      ...state,
      orderHeaderLoading: true,
    }),
  ),
  on(
    fromActions.downloadOrderInvoicesSuccess,
    fromActions.downloadOrderInvoicesFailure,
    (state): OrderDetailsState => ({
      ...state,
      orderHeaderLoading: false,
    }),
  ),
  on(
    fromActions.loadOrderDetailsSuccess,
    (
      state,
      { orderItems, details, products, orderHeader },
    ): OrderDetailsState =>
      adapter.setAll(orderItems, {
        ...state,
        loading: false,
        error: null,
        details,
        products,
        orderHeader,
      }),
  ),
  on(
    fromActions.loadOrderDetailsFailure,
    (state, { error }): OrderDetailsState =>
      adapter.removeAll({
        ...state,
        loading: false,
        error,
      }),
  ),
  on(
    fromActions.resetOrderDetailsState,
    (): OrderDetailsState => ({
      ...initialState,
    }),
  ),
);
